import { resumeAudioContext } from './resumeAudioContext.js';

export interface MicCaptureEvents {
  pcm: (chunk: ArrayBuffer) => void;
  rms: (value: number) => void;
  error: (err: Error) => void;
}

/**
 * Tramo del tap de instrumentación del worklet (ver {@link MicCapture.setProbe}).
 * `env` son pares `[rms, peak]` por bin de `binSamples` muestras; `pcm` son las
 * muestras CRUDAS (pre-gate) del tramo, sólo si se pidieron.
 *
 * Los dos `*StartTime` están en segundos del reloj del AudioContext del mic:
 * el bin `b` arranca en `envStartTime + (b * binSamples) / rate`.
 */
export interface ProbeDump {
  ok: true;
  rate: number;
  binSamples: number;
  pcmStartTime: number;
  envStartTime: number;
  env: Float32Array;
  pcm: Int16Array | null;
}

type WorkletMessage =
  | { type: 'pcm'; buf: ArrayBuffer }
  | { type: 'rms'; value: number }
  | { type: 'flushed'; id: number }
  | ({ type: 'probeDump'; id: number } & ({ ok: false } | ProbeDump));

/**
 * Mensaje del error con el que rechaza un `start()` cuyo bootstrap canceló un
 * `dispose()` (el mic NO falló: lo cancelaron). Constante compartida para que
 * el caller que decide si reintentar no matchee un literal suelto — si el
 * mensaje cambia acá, el matcheo lo sigue.
 */
export const MIC_DISPOSED_ERROR = 'mic_disposed';

/** ¿Este error es el `mic_disposed` de un bootstrap cancelado? */
export function isMicDisposedError(err: unknown): boolean {
  return err instanceof Error && err.message === MIC_DISPOSED_ERROR;
}

/**
 * Estado compartido del mic (stream + AudioContext + source node) de UN
 * scope. El scope por default es el de proceso (`processSharedMic`, el
 * singleton histórico); `createMicCapture()` crea uno propio para que dos
 * capturas puedan aislarse (ver su doc).
 */
interface SharedMic {
  readonly stream: MediaStream | null;
  readonly ctx: AudioContext | null;
  readonly source: MediaStreamAudioSourceNode | null;
  ensure(): Promise<void>;
  dispose(): Promise<void>;
}

function createSharedMic(): SharedMic {
  let sharedStream: MediaStream | null = null;
  let sharedCtx: AudioContext | null = null;
  let sharedSource: MediaStreamAudioSourceNode | null = null;
  /** Promise guard to prevent two concurrent `start()` calls from prompting twice. */
  let bootstrapping: Promise<void> | null = null;
  /**
   * Epoch del scope. `dispose()` lo incrementa para invalidar el bootstrap en
   * vuelo: si el cierre de sesión (o el `visibilitychange === 'hidden'`) cae
   * mientras el primer `start()` espera `getUserMedia`/`addModule`, la
   * adquisición que llega tarde NO debe asignarse — resucitaría el mic con la
   * sesión ya cerrada (track abierto, indicador rojo prendido y nadie que lo
   * libere). Es por scope, no global: el dispose de proceso no puede cancelar
   * el bootstrap de una captura de `createMicCapture()`.
   */
  let generation = 0;

  return {
    get stream() {
      return sharedStream;
    },
    get ctx() {
      return sharedCtx;
    },
    get source() {
      return sharedSource;
    },

    async ensure(): Promise<void> {
      if (bootstrapping) return bootstrapping;
      if (sharedStream && sharedCtx && sharedSource) {
        // iOS suspends the AudioContext when the PWA goes to background.
        await resumeAudioContext(sharedCtx, 'mic');
        return;
      }
      const gen = generation;
      const pending = (async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
          video: false,
        });
        const ctx = new AudioContext({ latencyHint: 'interactive' });
        // El worklet vive junto a este módulo en el package (no en el `public/` de
        // cada app). `new URL(..., import.meta.url)` hace que Vite lo emita como
        // asset propio y resuelva la URL correcta en dev, build web y build nativo
        // (Capacitor sirve desde `https://localhost`) — sin copias duplicadas por app.
        await ctx.audioWorklet.addModule(new URL('./pcm-worklet.js', import.meta.url));
        // Acotado: con autoplay bloqueado el `resume()` queda pendiente para
        // siempre (ver `resumeAudioContext`); el ctx arranca con el próximo gesto.
        await resumeAudioContext(ctx, 'mic');
        // Último chequeo DESPUÉS del último await y antes de asignar (de acá
        // en adelante todo es síncrono): si hubo un `dispose()` mientras
        // adquiríamos, soltamos lo que acabamos de tomar en vez de dejarlo
        // vivo. El `start()` que esperaba se entera con un error propio
        // (`mic_disposed`, no `mic_init_failed`): el mic no falló, lo
        // cancelaron — y el caller no debe confundirlo con un device roto.
        if (generation !== gen) {
          stream.getTracks().forEach((t) => t.stop());
          try { await ctx.close(); } catch { /* ignore */ }
          throw new Error(MIC_DISPOSED_ERROR);
        }
        const source = ctx.createMediaStreamSource(stream);
        sharedStream = stream;
        sharedCtx = ctx;
        sharedSource = source;
      })();
      bootstrapping = pending;
      // Sólo el bootstrap vigente limpia el guard: si `dispose()` ya lo soltó
      // y otro `start()` arrancó una adquisición nueva, no le pisamos la suya.
      try { await pending; } finally { if (bootstrapping === pending) bootstrapping = null; }
    },

    async dispose(): Promise<void> {
      // Invalida el bootstrap en vuelo (se libera solo al terminar) y suelta
      // el guard para que un `start()` posterior pueda adquirir de nuevo en
      // vez de quedar pegado a una adquisición que ya no va a asignar nada.
      generation += 1;
      bootstrapping = null;
      // Soltamos las TRES referencias antes de cualquier await, y liberamos
      // sobre copias locales: `ctx.close()` es asíncrono y en esa ventana un
      // `start()` puede adquirir con la generación ya nueva (gana
      // legítimamente) y publicar su stream/ctx/source. Si nuleáramos después
      // del await, la cola de este dispose le borraría ctx+source al mic nuevo
      // dejando el stream vivo y sin dueño: el `start()` siguiente no puede
      // reusar, re-adquiere, y el track anterior queda abierto para siempre.
      // El trigger real es el teardown de background, que NO awaitea el
      // dispose (`hidden` → `visible` rápido).
      const stream = sharedStream;
      const ctx = sharedCtx;
      sharedStream = null;
      sharedCtx = null;
      sharedSource = null;
      stream?.getTracks().forEach((t) => t.stop());
      try { await ctx?.close(); } catch { /* ignore */ }
    },
  };
}

/**
 * Process-wide mic singletons. Default de `new MicCapture()`. Lazily created
 * on the FIRST `MicCapture#start()` call and torn down via
 * `MicCapture.dispose()` when the voice session ends (and on
 * `visibilitychange === 'hidden'` / `pagehide`).
 *
 * Historical note: originalmente se mantenían vivos para todo el lifetime del
 * PWA porque iOS Safari "olvida" el permission de mic en cada acquire y
 * re-prompteaba al user. En la práctica eso resultó **más invasivo** que el
 * re-prompt: el browser muestra el indicador rojo de grabación PERMANENTE
 * (con el track abierto sin uso real), iOS hace ruidos de "audio reactivado"
 * al desbloquear el celu, y el user percibe que la app lo está escuchando
 * fuera de sesión. Para los browsers modernos (Chrome/Firefox/Edge desktop y
 * Android), una vez otorgado el permiso al origin el re-acquire es silencioso
 * — sólo parpadea el indicador. Para iOS PWA standalone, el permission queda
 * persistente por origin si el user no lo revocó manualmente; el "prompt"
 * del repermissioning suele ser silencioso (un chime del sistema, no el
 * dialog completo). La decisión actual prioriza la confianza del user sobre
 * un re-chime ocasional.
 *
 * El worklet sólo emite cuando un `MicCapture` instance está subscripto via
 * `source.connect(node)`; entre `stop()` (que desconecta el worklet) y
 * `dispose()` (que cierra el track), el mic no produce datos pero el OS lo
 * sigue marcando como "en uso". Por eso `dispose()` se llama al cerrar la
 * sesión, no `stop()` solo.
 */
const processSharedMic = createSharedMic();

/**
 * MediaStream del mic compartido de la sesión de voz, o `null` si todavía no
 * se adquirió (ningún `MicCapture#start()`) o ya se liberó (`dispose()`).
 * Para consumidores que necesitan grabar EN PARALELO al pipeline sobre el
 * MISMO stream (ej. el preview local "escucharme antes de enviar" del
 * composer de fit vía MediaRecorder) — un segundo `getUserMedia` sería más
 * caro y en iOS re-chimea el permiso.
 *
 * Refiere SIEMPRE al scope de proceso; las capturas de `createMicCapture()`
 * tienen su propio stream y no aparecen acá.
 */
export function getSharedMicStream(): MediaStream | null {
  return processSharedMic.stream;
}

/**
 * Captura con su PROPIO mic compartido, aislada del scope de proceso: hace su
 * propio `getUserMedia` y su `disposeShared()` no toca (ni es tocada por)
 * `MicCapture.dispose()`.
 *
 * Existe para que el mic sea testeable sin mutar estado global — hasta acá,
 * dos tests de audio consecutivos se pisaban: el segundo reusaba el stream
 * del primero y un `getUserMedia` que rechazaba ni siquiera se llamaba, así
 * que el path de permiso denegado era inescribible. En producción el default
 * sigue siendo el singleton (un solo permiso de mic por PWA); usá esta
 * factory sólo si el caller es dueño del ciclo de vida completo de su mic.
 */
export function createMicCapture(): MicCapture {
  return new MicCapture(createSharedMic());
}

export class MicCapture {
  /**
   * Scope del mic compartido al que está atada esta captura. Por default el
   * de proceso (comportamiento histórico: todas las instancias comparten un
   * único stream + AudioContext).
   */
  private readonly shared: SharedMic;
  private node: AudioWorkletNode | null = null;
  private listeners: Partial<MicCaptureEvents> = {};
  /**
   * Último RMS [0..1] emitido por el worklet. Refresca cada ~250ms (el chunk
   * size). Consumido por el orb-energy hook para animar visualmente la orb
   * en proporción a lo que efectivamente capta el mic.
   */
  private lastRms = 0;
  /** Pedidos de dump en vuelo, por id. Vacío salvo con el tap prendido. */
  private readonly probePending = new Map<number, (dump: ProbeDump | null) => void>();
  private probeSeq = 0;
  /** Flushes en vuelo, por id (ver {@link flush}). */
  private readonly flushPending = new Map<number, () => void>();
  private flushSeq = 0;

  constructor(shared: SharedMic = processSharedMic) {
    this.shared = shared;
  }

  on<K extends keyof MicCaptureEvents>(event: K, handler: MicCaptureEvents[K]) {
    this.listeners[event] = handler;
  }

  /** Último RMS [0..1] reportado por el worklet. `0` si nunca se midió. */
  getRms(): number {
    return this.lastRms;
  }

  async start() {
    if (this.node) return;
    await this.shared.ensure();
    const { ctx, source } = this.shared;
    if (!ctx || !source) throw new Error('mic_init_failed');

    const node = new AudioWorkletNode(ctx, 'pcm-downsampler', {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      processorOptions: { targetRate: 16000, chunkMs: 250 },
    });
    this.node = node;

    node.port.onmessage = (evt: MessageEvent<WorkletMessage>) => {
      const data = evt.data;
      if (data.type === 'pcm') this.listeners.pcm?.(data.buf);
      else if (data.type === 'rms') {
        this.lastRms = data.value;
        this.listeners.rms?.(data.value);
      } else if (data.type === 'flushed') {
        const done = this.flushPending.get(data.id);
        if (done) {
          this.flushPending.delete(data.id);
          done();
        }
      } else if (data.type === 'probeDump') {
        // Instrumentación (ver `requestProbeDump`). Rama muerta mientras nadie
        // prenda el tap: el worklet no emite este mensaje sin `setProbe`.
        const pending = this.probePending.get(data.id);
        if (pending) {
          this.probePending.delete(data.id);
          pending(data.ok ? data : null);
        }
      }
    };

    source.connect(node);
  }

  /**
   * Live-tune the near-field noise gate. `enabled=false` disables gating
   * entirely (useful for PTT/toggle where the user explicitly drives the mic
   * and a soft gate could clip a quiet whisper).
   *
   * ⚠️ APAGARLO NO ES GRATIS: el guard de partials del `SttProcessor`
   * (tercera capa anti-fantasma) se apoya en que este gate mande ceros
   * durante el silencio, y calibra su umbral 4× por debajo del de acá. Sin
   * gate, ese guard queda ciego y vuelve el turno fantasma que ataja (medido
   * 5/5, visto en prod). Ver `docs/architecture/04-voice-pipeline.md`
   * § anti-fantasma y el hallazgo #152 antes de cablear un caller.
   */
  setGate(opts: { enabled?: boolean; openThreshold?: number; closeThreshold?: number; holdMs?: number }) {
    if (!this.node) return;
    try { this.node.port.postMessage({ type: 'setGate', ...opts }); } catch { /* ignore */ }
  }

  /**
   * Avisa al worklet que se armó la ventana de settle (`MIC_SETTLE_MS`): vacía
   * el pre-roll del noise gate y no lo vuelve a juntar hasta que pasen `ms`.
   * Sin esto, un gate que abre apenas termina el settle traería en su pre-roll
   * audio de adentro de la ventana — eco del coach. Ver `pcm-worklet.js`.
   */
  armSettle(ms: number): void {
    if (!this.node) return;
    try { this.node.port.postMessage({ type: 'settle', ms }); } catch { /* ignore */ }
  }

  /**
   * Pide al worklet el bloque de PCM a medio llenar: sale YA por el handler de
   * `pcm` (con los gates de siempre) y la promesa resuelve cuando ese bloque ya
   * pasó — el `flushed` viaja detrás de él por el mismo puerto. Resuelve igual
   * a los `timeoutMs` (un AudioContext suspendido no contesta) o enseguida si
   * no hay worklet. Nunca rechaza: es el paso previo al `turn-stop`, que tiene
   * que salir de todos modos. Ver `pcm-worklet.js` § FLUSH.
   */
  flush(timeoutMs: number): Promise<void> {
    const node = this.node;
    if (!node) return Promise.resolve();
    const id = ++this.flushSeq;
    return new Promise<void>((resolve) => {
      const timer = setTimeout(() => {
        this.flushPending.delete(id);
        resolve();
      }, timeoutMs);
      this.flushPending.set(id, () => {
        clearTimeout(timer);
        resolve();
      });
      try {
        node.port.postMessage({ type: 'flush', id });
      } catch {
        clearTimeout(timer);
        this.flushPending.delete(id);
        resolve();
      }
    });
  }

  /**
   * Prende/apaga el TAP DE INSTRUMENTACIÓN del worklet: un ring buffer de las
   * muestras CRUDAS (pre-gate) más una envolvente de bins cortos, que después
   * se lee con {@link requestProbeDump}. No altera el PCM que se emite — es
   * sólo lectura. Apagado por default; nadie lo prende en el camino de
   * producción (lo usa el panel de medición de speak, opt-in por query param).
   *
   * `ringMs` (default 2000) es cuánto pasado retiene: el dump se pide DESPUÉS
   * del instante de interés, así que tiene que cubrir la latencia del main
   * thread. `binMs` (default 10) es la resolución de la envolvente.
   */
  setProbe(opts: { enabled: boolean; ringMs?: number; binMs?: number }): void {
    if (!this.node) return;
    try { this.node.port.postMessage({ type: 'setProbe', ...opts }); } catch { /* ignore */ }
  }

  /**
   * Pide el tramo `[fromTime, toTime]` del tap (tiempos en SEGUNDOS del reloj
   * del AudioContext del mic — el mismo `currentTime` que devuelve
   * {@link getAudioTimestamp}). Resuelve `null` si el tap está apagado o el
   * worklet no contesta antes de `timeoutMs`.
   */
  requestProbeDump(opts: {
    fromTime: number;
    toTime: number;
    pcm?: boolean;
    timeoutMs?: number;
  }): Promise<ProbeDump | null> {
    const node = this.node;
    if (!node) return Promise.resolve(null);
    const id = ++this.probeSeq;
    return new Promise<ProbeDump | null>((resolve) => {
      const timer = setTimeout(() => {
        this.probePending.delete(id);
        resolve(null);
      }, opts.timeoutMs ?? 1000);
      this.probePending.set(id, (dump) => {
        clearTimeout(timer);
        resolve(dump);
      });
      try {
        node.port.postMessage({
          type: 'probeDump',
          id,
          fromTime: opts.fromTime,
          toTime: opts.toTime,
          pcm: opts.pcm === true,
        });
      } catch {
        clearTimeout(timer);
        this.probePending.delete(id);
        resolve(null);
      }
    });
  }

  /**
   * Lectura APAREADA del reloj del AudioContext del mic y de `performance.now()`:
   * el puente para ubicar en wall-clock lo que el worklet fecha en el reloj de
   * audio. Los dos `read` van pegados, así que el error es el stall del main
   * thread entre ambos (décimas de ms) más la cuantización de `currentTime`
   * (un render quantum). `null` si todavía no hay AudioContext.
   *
   * NO corrige la latencia de ENTRADA del device (lo que tarda un sonido real
   * en aparecer en el worklet). Esa es una constante por teléfono y se mide
   * aparte, con un click de ida y vuelta.
   */
  getAudioTimestamp(): { contextTime: number; performanceTime: number; sampleRate: number } | null {
    const ctx = this.shared.ctx;
    if (!ctx) return null;
    const contextTime = ctx.currentTime;
    const performanceTime = performance.now();
    return { contextTime, performanceTime, sampleRate: ctx.sampleRate };
  }

  /**
   * Detach this instance's worklet node from the shared source. Does NOT
   * stop the underlying MediaStream or close the AudioContext. Para liberar
   * el mic del todo (necesario al cerrar la sesión, para apagar el indicador
   * rojo del browser y los ruidos de iPhone), llamar `MicCapture.dispose()`.
   */
  stop(): Promise<void> {
    const node = this.node;
    this.node = null;
    if (!node) return Promise.resolve();
    try { this.shared.source?.disconnect(node); } catch { /* ignore */ }
    try { node.disconnect(); } catch { /* ignore */ }
    try { node.port.onmessage = null; } catch { /* ignore */ }
    // Sin worklet no va a llegar ningún `flushed`: que el commit no espere.
    for (const done of this.flushPending.values()) done();
    this.flushPending.clear();
    return Promise.resolve();
  }

  /**
   * Libera del todo el MediaStream + AudioContext compartidos de ESTA
   * instancia. Idempotente. Para una instancia del scope de proceso (el
   * default) equivale a `MicCapture.dispose()`; para una de
   * `createMicCapture()`, libera únicamente su mic aislado — el estático NO
   * la alcanza.
   */
  disposeShared(): Promise<void> {
    return this.shared.dispose();
  }

  /**
   * Libera del todo el MediaStream + AudioContext compartidos DEL SCOPE DE
   * PROCESO. Idempotente. Llamado por `useVoiceSession.cleanup()` al cerrar
   * la sesión y por los listeners de `visibilitychange === 'hidden'` /
   * `pagehide` para que el indicador rojo del browser y el track del OS se
   * apaguen cuando el user no está activamente charlando.
   *
   * No toca las capturas creadas con `createMicCapture()`: cada una es dueña
   * de su mic y se libera con `disposeShared()`.
   */
  static async dispose() {
    await processSharedMic.dispose();
  }
}
